import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Container,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Switch,
  FormControlLabel,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff, 
  Lock, 
  Email,
  DarkMode,
  LightMode 
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { loginUser } from "../../store/UserSlice";
import { LayoutDashboard } from "lucide-react";
import { isAuthenticated } from "../../config/config";

// Theme colors
const BRAND_TEAL = "#45B9BC";
const BRAND_BLACK = "#000000";
const DARK_BG = "#1a1a1a";
const DARK_CARD = "#2d2d2d";
const DARK_TEXT = "#ffffff";

// Create dynamic theme
const createAppTheme = (darkMode) => createTheme({
  palette: {
    mode: darkMode ? 'dark' : 'light',
    primary: {
      main: BRAND_TEAL,
      contrastText: '#ffffff',
    },
    secondary: {
      main: BRAND_BLACK,
    },
    background: {
      default: darkMode ? DARK_BG : '#f8f9fa',
      paper: darkMode ? DARK_CARD : '#ffffff',
    },
    text: {
      primary: darkMode ? DARK_TEXT : BRAND_BLACK,
      secondary: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Main container with theme-aware styling
const LoginContainer = styled(Box)(({ theme, darkmode }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: darkmode === 'true' 
    ? `linear-gradient(135deg, ${DARK_BG} 0%, #2d2d2d 50%, ${BRAND_BLACK} 100%)`
    : `linear-gradient(135deg, ${BRAND_TEAL} 0%, #36a8ab 50%, #2d9194 100%)`,
  padding: theme.spacing(2),
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s ease-in-out",
  
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background: darkmode === 'true'
      ? "radial-gradient(circle, rgba(69, 185, 188, 0.1) 1px, transparent 1px)"
      : "radial-gradient(circle, rgba(255, 255, 255, 0.2) 1px, transparent 1px)",
    backgroundSize: "30px 30px",
    animation: "float 20s ease-in-out infinite",
    zIndex: 0,
  },
  
  "@keyframes float": {
    "0%, 100%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-20px)" },
  },
}));

const LoginCard = styled(Card)(({ theme, darkmode }) => ({
  display: "flex",
  borderRadius: "24px",
  overflow: "hidden",
  maxWidth: "1100px",
  width: "100%",
  minHeight: "600px",
  boxShadow: darkmode === 'true' 
    ? "0 20px 60px rgba(0, 0, 0, 0.8)"
    : "0 20px 60px rgba(0, 0, 0, 0.3)",
  backdropFilter: "blur(20px)",
  border: darkmode === 'true'
    ? "1px solid rgba(69, 185, 188, 0.3)"
    : "1px solid rgba(255, 255, 255, 0.3)",
  position: "relative",
  zIndex: 1,
  backgroundColor: darkmode === 'true' ? DARK_CARD : '#ffffff',
  transition: "all 0.3s ease-in-out",
  
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    maxWidth: "400px",
    minHeight: "auto",
    margin: theme.spacing(1),
  },
}));

const LeftSide = styled(Box)(({ theme, darkmode }) => ({
  width: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: darkmode === 'true'
    ? `linear-gradient(135deg, ${DARK_CARD} 0%, #3a3a3a 100%)`
    : `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
  padding: theme.spacing(4),
  position: "relative",
  transition: "all 0.3s ease-in-out",
  
  [theme.breakpoints.down("md")]: {
    width: "100%",
    minHeight: "200px",
    padding: theme.spacing(2),
  },
  
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: darkmode === 'true'
      ? `radial-gradient(circle at 50% 50%, ${BRAND_TEAL}20 0%, transparent 50%)`
      : `radial-gradient(circle at 50% 50%, ${BRAND_TEAL}10 0%, transparent 50%)`,
    opacity: 0.3,
  },
}));

const RightSide = styled(Box)(({ theme, darkmode }) => ({
  width: "50%",
  background: darkmode === 'true'
    ? `linear-gradient(135deg, ${BRAND_BLACK} 0%, ${DARK_BG} 100%)`
    : `linear-gradient(135deg, ${BRAND_TEAL} 0%, #36a8ab 100%)`,
  padding: theme.spacing(5),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  color: "#ffffff",
  position: "relative",
  transition: "all 0.3s ease-in-out",
  
  [theme.breakpoints.down("md")]: {
    width: "100%",
    padding: theme.spacing(3),
  },
  
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: darkmode === 'true'
      ? "linear-gradient(45deg, rgba(69, 185, 188, 0.1) 25%, transparent 25%, transparent 75%, rgba(69, 185, 188, 0.1) 75%), linear-gradient(45deg, rgba(69, 185, 188, 0.1) 25%, transparent 25%, transparent 75%, rgba(69, 185, 188, 0.1) 75%)"
      : "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%, transparent 75%, rgba(255, 255, 255, 0.1) 75%), linear-gradient(45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%, transparent 75%, rgba(255, 255, 255, 0.1) 75%)",
    backgroundSize: "20px 20px",
    backgroundPosition: "0 0, 10px 10px",
    opacity: 0.3,
  },
}));

const DarkModeToggle = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 10,
  display: "flex",
  alignItems: "center",
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  borderRadius: "25px",
  padding: theme.spacing(0.5, 1),
  border: "1px solid rgba(255, 255, 255, 0.2)",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  padding: theme.spacing(1),
  borderRadius: "10px",
  background: "rgba(255, 255, 255, 0.15)",
  backdropFilter: "blur(15px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  position: "relative",
  zIndex: 2,
}));

const StyledTextField = styled(TextField)(({ theme, darkmode }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  
  "& .MuiOutlinedInput-root": {
    backgroundColor: darkmode === 'true' 
      ? "rgba(255, 255, 255, 0.1)" 
      : "rgba(255, 255, 255, 0.95)",
    borderRadius: "12px",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease-in-out",
    
    "& fieldset": {
      borderColor: darkmode === 'true' 
        ? "rgba(69, 185, 188, 0.5)" 
        : "rgba(255, 255, 255, 0.4)",
      borderWidth: "2px",
    },
    
    "&:hover fieldset": {
      borderColor: BRAND_TEAL,
      transform: "scale(1.02)",
    },
    
    "&.Mui-focused fieldset": {
      borderColor: BRAND_TEAL,
      boxShadow: `0 0 20px ${BRAND_TEAL}40`,
    },
  },
  
  "& .MuiInputBase-input": {
    color: darkmode === 'true' ? "#ffffff" : BRAND_BLACK,
    fontSize: "16px",
    padding: theme.spacing(1.5),
    
    "&::placeholder": {
      color: darkmode === 'true' ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.5)",
    },
  },
}));

const LoginButton = styled(Button)(({ theme, darkmode }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  fontSize: "16px",
  fontWeight: "600",
  borderRadius: "12px",
  background: darkmode === 'true'
    ? `linear-gradient(135deg, ${BRAND_TEAL} 0%, #36a8ab 100%)`
    : `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
  color: darkmode === 'true' ? "#ffffff" : BRAND_BLACK,
  boxShadow: darkmode === 'true'
    ? `0 8px 25px ${BRAND_TEAL}40`
    : "0 8px 25px rgba(255, 255, 255, 0.4)",
  textTransform: "none",
  transition: "all 0.3s ease-in-out",
  position: "relative",
  zIndex: 2,
  
  "&:hover": {
    background: darkmode === 'true'
      ? `linear-gradient(135deg, #36a8ab 0%, ${BRAND_TEAL} 100%)`
      : `linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)`,
    transform: "translateY(-2px)",
    boxShadow: darkmode === 'true'
      ? `0 12px 35px ${BRAND_TEAL}60`
      : "0 12px 35px rgba(255, 255, 255, 0.5)",
  },
  
  "&:active": {
    transform: "translateY(0px)",
  },
  
  "&:disabled": {
    background: darkmode === 'true' 
      ? "rgba(69, 185, 188, 0.3)" 
      : "rgba(255, 255, 255, 0.5)",
    color: darkmode === 'true' 
      ? "rgba(255, 255, 255, 0.5)" 
      : "rgba(0, 0, 0, 0.3)",
  },
}));

const IllustrationContainer = styled(Box)(({ theme, darkmode }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  zIndex: 2,
  
  "& img": {
    maxWidth: "85%",
    height: "auto",
    borderRadius: "16px",
    boxShadow: darkmode === 'true'
      ? `0 10px 30px rgba(0, 0, 0, 0.5)`
      : "0 10px 30px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.3s ease-in-out",
    filter: darkmode === 'true' ? "brightness(0.9)" : "brightness(1)",
    
    "&:hover": {
      transform: "scale(1.05) rotate(1deg)",
    },
  },
  
  // Fallback design if image doesn't load
  "&::after": {
    content: '""',
    position: "absolute",
    width: "200px",
    height: "200px",
    background: `linear-gradient(135deg, ${BRAND_TEAL} 0%, #36a8ab 100%)`,
    borderRadius: "50%",
    opacity: 0.1,
    zIndex: -1,
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
}));

export default function SignIn() {
  // Pre-fill with test credentials for development
  const [email, setEmail] = useState(process.env.NODE_ENV === 'development' ? "admin@example.com" : "");
  const [password, setPassword] = useState(process.env.NODE_ENV === 'development' ? "admin123" : "");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    // Load dark mode preference from localStorage
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { error, loading } = useSelector((state) => state.user);
  const isAuth = isAuthenticated();
  
  // Create theme based on dark mode
  const appTheme = createAppTheme(darkMode);

  useEffect(() => {
    setIsLoaded(true);
    if (isAuth) {
      navigate("/", { replace: true });
    }
  }, [isAuth, navigate]);

  useEffect(() => {
    // Save dark mode preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const handleSubmit = (event) => {
    event.preventDefault();
    console.log("Login attempt with:", { email });
    
    dispatch(loginUser({ email, password })).then((result) => {
      console.log("Login result:", result.meta.requestStatus);
      
      if (result.meta.requestStatus === "fulfilled") {
        console.log("Login successful, navigating to dashboard");
        navigate("/", { replace: true });
      } else {
        console.warn("Login failed:", result.payload);
      }
    });
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <ThemeProvider theme={appTheme}>
      <LoginContainer darkmode={darkMode.toString()}>
        {/* Dark Mode Toggle */}
        <DarkModeToggle>
          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={toggleDarkMode}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: BRAND_TEAL,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: BRAND_TEAL,
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffffff' }}>
                {darkMode ? <DarkMode /> : <LightMode />}
                <Typography variant="caption" sx={{ color: '#ffffff' }}>
                  {darkMode ? 'Dark' : 'Light'}
                </Typography>
              </Box>
            }
          />
        </DarkModeToggle>

        <Container maxWidth="lg" sx={{ display: "flex", justifyContent: "center" }}>
          <Fade in={isLoaded} timeout={800}>
            <LoginCard darkmode={darkMode.toString()}>
              {/* Left Side - Illustration */}
              <LeftSide darkmode={darkMode.toString()}>
                <Slide direction="right" in={isLoaded} timeout={1000}>
                  <IllustrationContainer darkmode={darkMode.toString()}>
                    <img
                      src={`${process.env.PUBLIC_URL}/loginpage.jpg`}
                      alt="Login Illustration"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </IllustrationContainer>
                </Slide>
              </LeftSide>

              {/* Right Side - Login Form */}
              <RightSide darkmode={darkMode.toString()}>
                <Slide direction="left" in={isLoaded} timeout={1200}>
                  <FormContainer>
                    {/* Logo Section */}
                    <LogoContainer>
                      <LayoutDashboard 
                        style={{ 
                          width: "32px", 
                          height: "32px", 
                          color: "#ffffff" 
                        }} 
                      />
                      <Typography
                        variant="h5"
                        sx={{ 
                          fontWeight: "700", 
                          fontSize: isMobile ? "1.3rem" : "1.5rem",
                          letterSpacing: "1px",
                          color: "#ffffff"
                        }}
                      >
                        GABS
                      </Typography>
                    </LogoContainer>

                    {/* Welcome Section */}
                    <Box sx={{ mb: 4, textAlign: "center" }}>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: "700", 
                          mb: 1,
                          fontSize: isMobile ? "1.8rem" : "2.2rem",
                          background: darkMode 
                            ? `linear-gradient(135deg, ${BRAND_TEAL} 0%, #ffffff 100%)`
                            : "linear-gradient(135deg, #ffffff 0%, #e8f2ff 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        Anmelden
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          opacity: 0.9,
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          color: "#ffffff"
                        }}
                      >
                        Willkommen bei Adminis
                      </Typography>
                    </Box>

                    {/* Login Form */}
                    <Box
                      component="form"
                      onSubmit={handleSubmit}
                      autoComplete="off"
                      sx={{ mt: 2 }}
                    >
                      {/* Email Field */}
                      <Typography
                        variant="body2"
                        sx={{ 
                          color: "#ffffff", 
                          fontWeight: "600", 
                          mb: 1,
                          opacity: 0.9,
                        }}
                      >
                        E-Mail-Adresse
                      </Typography>
                      <StyledTextField
                        variant="outlined"
                        fullWidth
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="ihre@email.com"
                        darkmode={darkMode.toString()}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 1 }}>
                              <Email sx={{ color: BRAND_TEAL }} />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {/* Password Field */}
                      <Typography
                        variant="body2"
                        sx={{ 
                          color: "#ffffff", 
                          fontWeight: "600", 
                          mb: 1, 
                          mt: 2,
                          opacity: 0.9,
                        }}
                      >
                        Passwort
                      </Typography>
                      <StyledTextField
                        variant="outlined"
                        fullWidth
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Ihr Passwort eingeben"
                        darkmode={darkMode.toString()}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 1 }}>
                              <Lock sx={{ color: BRAND_TEAL }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                sx={{ 
                                  color: BRAND_TEAL,
                                  "&:hover": { 
                                    backgroundColor: `${BRAND_TEAL}20` 
                                  }
                                }}
                              >
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      {/* Error Alert */}
                      {error && (
                        <Fade in={!!error}>
                          <Alert 
                            severity="error" 
                            sx={{ 
                              mt: 2, 
                              borderRadius: "12px",
                              backgroundColor: darkMode 
                                ? "rgba(244, 67, 54, 0.2)" 
                                : "rgba(244, 67, 54, 0.1)",
                              border: "1px solid rgba(244, 67, 54, 0.4)",
                              color: "#ffffff",
                              "& .MuiAlert-icon": {
                                color: "#ff6b6b",
                              },
                            }}
                          >
                            {error}
                          </Alert>
                        </Fade>
                      )}

                      {/* Login Button */}
                      <LoginButton
                        type="submit"
                        fullWidth
                        disabled={loading || !email || !password}
                        darkmode={darkMode.toString()}
                      >
                        {loading ? "Anmelden..." : "Anmelden"}
                      </LoginButton>

                      {/* Footer Text */}
                      <Typography
                        variant="body2"
                        sx={{ 
                          textAlign: "center", 
                          mt: 3, 
                          opacity: 0.8,
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          color: "#ffffff"
                        }}
                      >
                        Kein Mitglied?{" "}
                        <Box 
                          component="span" 
                          sx={{ 
                            fontWeight: "600", 
                            textDecoration: "underline",
                            cursor: "pointer",
                            color: darkMode ? BRAND_TEAL : "#ffffff",
                            "&:hover": { opacity: 0.8 }
                          }}
                        >
                          Kontaktieren Sie uns
                        </Box>
                      </Typography>
                    </Box>
                  </FormContainer>
                </Slide>
              </RightSide>
            </LoginCard>
          </Fade>
        </Container>
      </LoginContainer>
    </ThemeProvider>
  );
}